/**
 * ScheduleModal
 * Pianifica l'invio di un messaggio con calendario custom
 */

import { useState, useMemo } from 'react';
import { X, Clock, ChevronLeft, ChevronRight, Loader2, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const DAYS = ['Lu','Ma','Me','Gi','Ve','Sa','Do'];

const pad2 = (n: number) => String(n).padStart(2, '0');
const nowHHMM = () => { const n = new Date(); return `${pad2(n.getHours())}:${pad2(n.getMinutes())}`; };
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

interface ScheduleModalProps {
  chatId: string;
  messageText: string;
  onClose: () => void;
  onScheduled: () => void;
}

export function ScheduleModal({ chatId, messageText, onClose, onScheduled }: ScheduleModalProps) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);

  const [currentMonth, setCurrentMonth] = useState(tomorrow.getMonth());
  const [currentYear, setCurrentYear] = useState(tomorrow.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date>(tomorrow);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { wahaConfig } = useWhatsAppChatStore();

  const getDaysInMonth = (m: number, y: number) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (m: number, y: number) => {
    const day = new Date(y, m, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const calendarDays = useMemo(() => {
    const count = getDaysInMonth(currentMonth, currentYear);
    const first = getFirstDayOfMonth(currentMonth, currentYear);
    const days: (number | null)[] = Array(first).fill(null);
    for (let i = 1; i <= count; i++) days.push(i);
    return days;
  }, [currentMonth, currentYear]);

  const isDateInPast = (day: number) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(currentYear, currentMonth, day) < today;
  };

  const isSelected = (day: number) =>
    selectedDate.getDate() === day &&
    selectedDate.getMonth() === currentMonth &&
    selectedDate.getFullYear() === currentYear;

  const handleDateSelect = (day: number) => {
    if (isDateInPast(day)) return;
    const d = new Date(currentYear, currentMonth, day);
    setSelectedDate(d);
    // Se scelgo oggi e l'orario selezionato è già passato, lo porto all'ora attuale
    if (isSameDay(d, new Date()) && selectedTime < nowHHMM()) {
      setSelectedTime(nowHHMM());
    }
  };

  // Orario minimo selezionabile: ora attuale se la data è oggi, altrimenti 00:00
  const minTime = isSameDay(selectedDate, new Date()) ? nowHHMM() : '00:00';

  const handleSchedule = async () => {
    if (!wahaConfig?.organizationId) { setError('Config WAHA non trovata'); return; }

    const [selHour, selMinute] = selectedTime.split(':').map(Number);
    const scheduledAt = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      selHour,
      selMinute
    );

    if (scheduledAt <= new Date()) { setError('Seleziona un orario futuro'); return; }

    setScheduling(true);
    setError(null);
    try {
      // 🚨 7 set 2026: qui NON veniva salvato `config_id`, cioè da quale sessione
      // WhatsApp far partire il messaggio (19 righe su 22 in produzione ce l'hanno
      // vuoto). Conseguenze reali:
      //  - chi invia viene scelto dopo, sulla configurazione "globale" dell'org: da qui
      //    il messaggio di Marco recapitato a nome di Stefano (segnalazione del 27/04);
      //  - se l'org non ha una configurazione globale attiva (oggi è il caso di Obly),
      //    il messaggio non parte MAI, ma risulta comunque "inviato" perché lo stato
      //    viene marcato in modo ottimistico.
      // Si salva la sessione con cui l'utente sta scrivendo, che è quella giusta.
      if (!wahaConfig.configId) {
        setError('Sessione WhatsApp non identificata: riapri la chat e riprova');
        setScheduling(false);
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      const { error: dbError } = await supabase.from('whatsapp_scheduled_messages').insert({
        organization_id: wahaConfig.organizationId,
        config_id: wahaConfig.configId,
        chat_jid: chatId,
        content: messageText,
        type: 'text',
        scheduled_at: scheduledAt.toISOString(),
        created_by: userData.user?.id,
      });
      if (dbError) throw dbError;
      onScheduled();
    } catch (err: any) {
      setError(err.message || 'Errore durante la pianificazione');
      setScheduling(false);
    }
  };

  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };
  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <Calendar className="w-5 h-5 text-[#787774]" />
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Programma invio</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Preview messaggio */}
          <div className="text-xs text-[#787774] bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg px-3 py-2 line-clamp-2">
            {messageText}
          </div>

          {/* Calendario */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <button onClick={goToPrevMonth} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
                <ChevronLeft className="w-4 h-4 text-[#787774]" />
              </button>
              <span className="text-sm font-medium text-[#37352F] dark:text-white">
                {MONTHS[currentMonth]} {currentYear}
              </span>
              <button onClick={goToNextMonth} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
                <ChevronRight className="w-4 h-4 text-[#787774]" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS.map((d) => (
                <div key={d} className="text-center text-[10px] font-medium text-[#787774] py-1">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {calendarDays.map((day, i) => (
                <div key={i} className="aspect-square flex items-center justify-center">
                  {day ? (
                    <button
                      onClick={() => handleDateSelect(day)}
                      disabled={isDateInPast(day)}
                      className={`w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                        isSelected(day)
                          ? 'bg-[#2383E2] text-white'
                          : isDateInPast(day)
                          ? 'text-[#787774]/40 cursor-not-allowed'
                          : 'text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10'
                      }`}
                    >
                      {day}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          {/* Orario — input libero, con minimo = ora attuale se la data è oggi */}
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-[#787774] flex-shrink-0" />
            <input
              type="time"
              value={selectedTime}
              min={minTime}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                // Non permettere un orario nel passato quando la data è oggi
                setSelectedTime(isSameDay(selectedDate, new Date()) && v < minTime ? minTime : v);
              }}
              className="flex-1 text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-1.5 outline-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
          )}

          <button
            onClick={handleSchedule}
            disabled={scheduling}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 rounded-lg transition-colors"
          >
            {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
            {scheduling ? 'Pianificazione...' : `Programma per ${MONTHS[selectedDate.getMonth()].slice(0,3)} ${selectedDate.getDate()} alle ${selectedTime}`}
          </button>
        </div>
      </div>
    </div>
  );
}
